export type TAssetStatus =
  | '未確認'
  | '確認済'
  | '未使用'
  | '不記載'
  | '移動'
  | '除却';

export interface TAssetDetail {
  assetId: string;
  assetNo: string;
  company: string;
  store: string;
  assetType: string;
  acquisitionDate: string;
  usefulLife: number;
  acquisitionCost: number | null;
  bookValueStart: number | null;
  updatedAt: string;
  hasPhoto: boolean;
  assetName: string;
  department: string;
  departmentCode: string;
  location: string;
  quantity: number | null;
  sealRequest: number | null;
  remarks: string;
  status: TAssetStatus;
  source: string | null;
}

export interface TChangedField {
  label: string;
  value: string;
}

/** StatusCode (BE) ↔ label (JP display) mapping. Contract: api-integration.md §3. */
export const STATUS_CODE_TO_LABEL: Record<string, TAssetStatus> = {
  UNCONFIRMED: '未確認',
  CONFIRMED: '確認済',
  UNUSED: '未使用',
  UNLISTED: '不記載',
  MOVE: '移動',
  REMOVE: '除却',
};

export const STATUS_LABEL_TO_CODE: Record<TAssetStatus, string> = {
  未確認: 'UNCONFIRMED',
  確認済: 'CONFIRMED',
  未使用: 'UNUSED',
  不記載: 'UNLISTED',
  移動: 'MOVE',
  除却: 'REMOVE',
};

/**
 * BE-PENDING: Fallback mock for GET /api/v1/assets/{assetId} on load failure only.
 * Used only by unit tests in __tests__/ — do NOT use in production code paths.
 * Remove once real endpoint is stable.
 */
export const MOCK_ASSET: TAssetDetail = {
  assetId: '1',
  assetNo: '20527-001',
  company: 'SM',
  store: '002 明智',
  assetType: '器具及び備品',
  acquisitionDate: '2015/11/30',
  usefulLife: 5,
  acquisitionCost: 417000,
  bookValueStart: 85200,
  updatedAt: '2019/1/20',
  hasPhoto: true,
  assetName: '青葉センター向け店舗支援カメラ',
  department: '店舗管理部',
  location: '1F レジ',
  quantity: 1,
  sealRequest: 0,
  remarks: '',
  status: '未確認',
  source: null,
  departmentCode: '',
};

/**
 * Hard-coded department options — codes 1/2/3/6/12/19 are named; 999 = その他.
 * Shared with PC004 (FixedAssetLedger/constant.ts OPTIONS.DEPARTMENT, excluding 全て).
 */
export const DEPARTMENT_OPTIONS: { label: string; value: string }[] = [
  { label: '畜産', value: '1' },
  { label: '水産', value: '2' },
  { label: '農産', value: '19' },
  { label: '野菜', value: '3' },
  { label: 'ベーク', value: '12' },
  { label: 'デリカ', value: '6' },
  { label: 'その他', value: '999' },
];

/**
 * BE-PENDING (BE-8): GET /api/v1/assets/lookups not yet created.
 * Used as fallback when the lookups endpoint is unavailable.
 */
export const MOCK_LOCATIONS: { label: string; value: string }[] = [
  { label: '1F レジ', value: '1F レジ' },
  { label: '1F 売場', value: '1F 売場' },
  { label: '2F 事務所', value: '2F 事務所' },
  { label: 'バックヤード', value: 'バックヤード' },
];

export const FIXED_ASSET_DETAIL_CONSTANT = {
  TITLE: {
    PAGE: '固定資産明細メンテ',
    PARENT: '固定資産管理台帳',
    BTN_BACK: '← 戻る',
    BTN_BACK_FOOTER: '戻る',
    BTN_SAVE: '保存',
    BTN_CANCEL: 'キャンセル',
    BTN_OK: 'OK',
    READONLY_SECTION: '参照のみ / 編集不可',
    EDITABLE_SECTION: '編集可能項目',
    STATUS_SECTION: 'ステータス変更',
    SAVE_CONFIRM_TITLE: '保存確認',
    SAVE_CONFIRM_MSG: '以下の内容で保存してもよろしいですか？',
    CHANGE_SUMMARY: '変更内容サマリー',
    NO_CHANGES: '変更された項目はありません。',
  },
  LABEL: {
    ASSET_NO: '資産番号',
    COMPANY: '会社',
    STORE: '店舗',
    ASSET_TYPE: '種類',
    ACQUISITION_DATE: '取得年月日',
    USEFUL_LIFE: '耐用年数',
    ACQUISITION_COST: '取得価額',
    BOOK_VALUE_START: '期首帳簿価額',
    UPDATED_AT: '最新年月日',
    HAS_PHOTO: '写真有無',
    ASSET_NAME: '資産名称（必須）',
    ASSET_NAME_PLACEHOLDER: '資産名称',
    DEPARTMENT: '部門',
    LOCATION: '所属',
    QUANTITY: '数量',
    SEAL_REQUEST: 'タグ要求',
    REMARKS: '備考',
    CURRENT_STATUS: '現在のステータス',
    TARGET_STATUS: '変更後ステータス',
    STATUS_NOTE: '不記載 以外のステータスに変更できます',
    STATUS_LOCKED_NOTE: 'このステータスはここでは変更できません',
  },
  NAME: {
    ASSET_NAME: 'assetName',
    DEPARTMENT: 'department',
    LOCATION: 'location',
    QUANTITY: 'quantity',
    SEAL_REQUEST: 'sealRequest',
    REMARKS: 'remarks',
  },
  MESSAGE: {
    V01: '資産名称は必須入力です。',
    V02: '数量は正の整数で入力してください。',
    V03: 'タグ要求は0以上の整数で入力してください。',
    S01: '保存が完了しました。',
    E01: 'データの取得に失敗しました。再度お試しください。',
    E02: '保存に失敗しました。再度お試しください。',
  },
  PHOTO: {
    EXISTS: '✔ あり',
    NONE: 'なし',
  },
};

// PC005: ADMIN (kế toán trưởng) có thể đổi sang bất kỳ status nào, trừ 不記載 (chỉ qua luồng iPhone).
export const SELECTABLE_STATUSES: TAssetStatus[] = [
  '未確認',
  '確認済',
  '未使用',
  '移動',
  '除却',
];
// 不記載 là kết quả riêng của luồng kiểm kê (iPhone flow) — không cho đổi qua PC005, kể cả ADMIN.
export const LOCKED_STATUSES: TAssetStatus[] = ['不記載'];

// Màu badge khớp với PC004 (FixedAssetLedger/columns.tsx) cho 移動/除却 để nhất quán toàn app.
export const STATUS_BADGE_CONFIG: Record<
  TAssetStatus,
  { color: string; bg: string }
> = {
  未確認: { color: '#6b7280', bg: '#f3f4f6' },
  確認済: { color: '#1677ff', bg: '#e6f4ff' },
  未使用: { color: '#f97316', bg: '#fff7ed' },
  不記載: { color: '#ef4444', bg: '#fef2f2' },
  移動: { color: '#9e77ed', bg: '#f6f3fe' },
  除却: { color: '#344054', bg: '#f2f4f7' },
};
